import { AmountDisplay } from "@/app/shared/ui/amount-display";
import { Badge } from "@groot/ui/badge";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { Button } from "@groot/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@groot/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@groot/ui/dialog";
import { Input } from "@groot/ui/input";
import { Label } from "@groot/ui/label";
import { LoadingSpinner } from "@groot/ui/loading-spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { Textarea } from "@groot/ui/textarea";
import { toast } from "@/lib/toast";
import { apiClient } from "@/services/api";
import type { Receipt } from "@/types";
import dayjs from "dayjs";
import { Eye, Mail, Plus, Receipt as ReceiptIcon, Trash2, Upload } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function Receipts() {
  const navigate = useNavigate();

  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");

  const loadReceipts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getReceipts();
      setReceipts(data);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load receipts",
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadReceipts();
  }, [loadReceipts]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      await apiClient.uploadAndExtractReceipt(selectedFile, notes || undefined);
      toast({
        title: "Success",
        description: "Receipt uploaded and extracted successfully",
      });
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setNotes("");
      loadReceipts();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to upload receipt",
      });
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.deleteReceipt(id);
      toast({
        title: "Success",
        description: "Receipt deleted successfully",
      });
      loadReceipts();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete receipt",
      });
      console.error(err);
    }
  };

  const formatDate = (date?: string | null) => {
    if (!date) return "—";
    return dayjs(date).format("MMM D, YYYY");
  };

  const formatFileSize = (bytes?: number | null) => {
    if (bytes == null) return "Unknown";
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  if (loading) {
    return (
      <Page>
        <LoadingSpinner size="lg" className="py-20" />
      </Page>
    );
  }

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Receipts"
        description="Upload and manage your receipts with AI extraction"
        className="mb-8"
        actions={
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="size-4" />
                Upload Receipt
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Upload Receipt</DialogTitle>
                <DialogDescription>Upload a PDF receipt to extract data using AI</DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="file">PDF File</Label>
                  <Input id="file" type="file" accept=".pdf" onChange={handleFileChange} />
                  {selectedFile && (
                    <p className="text-sm text-muted-foreground">
                      {selectedFile.name} ({formatFileSize(selectedFile.size)})
                    </p>
                  )}
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Add any notes about this receipt..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpload} disabled={!selectedFile || uploading}>
                  {uploading ? (
                    <>
                      <Upload className="size-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="size-4" />
                      Upload & Extract
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Email Ingestion Info */}
      <Card className="mb-6 bg-info/10 border-info/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Mail className="size-4" />
            Email Ingestion
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          <p>
            You can also send receipts via email. PDF attachments will be automatically processed
            and extracted using AI.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Configure <code className="bg-background px-1 rounded">RECEIPT_EMAIL_ADDRESS</code> in
            your environment to enable this feature.
          </p>
        </CardContent>
      </Card>

      {/* Receipts List */}
      {receipts.length === 0 ? (
        <Card>
          <CardHeader className="text-center py-12">
            <ReceiptIcon className="mx-auto size-12 text-muted-foreground/70" />
            <CardTitle className="mt-4">No receipts yet</CardTitle>
            <CardDescription>Upload your first receipt to get started</CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>All Receipts</CardTitle>
            <CardDescription>
              {receipts.length} receipt{receipts.length !== 1 ? "s" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receipts.map((receipt) => (
                  <TableRow key={receipt.id}>
                    <TableCell className="font-medium">
                      {receipt.merchantName || "Unknown Merchant"}
                    </TableCell>
                    <TableCell>{formatDate(receipt.receiptDate)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">
                        {receipt.totalAmount != null && (
                          <AmountDisplay
                            amount={receipt.totalAmount}
                            currencyCode={receipt.currency || "INR"}
                          />
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
                        {receipt.fileName}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/receipts/${receipt.id}`)}
                        >
                          <Eye className="size-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(receipt.id)}>
                          <Trash2 className="size-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </Page>
  );
}
